import React, { useState, useMemo, useRef, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Printer, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useToast } from '../../components/ui/Toast';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { devLog, devWarn } from '../../utils/devLog';

type QRCodeType = 'company' | string; // 'company' or location ID

export default function SignupQRPage() {
  const { data: companyId } = useCurrentCompany();
  const [selectedQRType, setSelectedQRType] = useState<QRCodeType>('company');
  const qrRef = useRef<HTMLDivElement>(null);
  const { showToast, ToastContainer } = useToast();

  // Fetch company data
  const { data: companyData } = useQuery({
    queryKey: ['company', companyId],
    enabled: !!companyId,
    queryFn: async () => {
      if (!companyId) return null;
      const { data, error } = await (supabase as any)
        .from('companies')
        .select('id, name, logo_url')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Fetch company locations
  const { data: locations = [], isLoading: locationsLoading } = useQuery({
    queryKey: ['company_locations', companyId],
    enabled: !!companyId,
    queryFn: async () => {
      if (!companyId) return null;
      const { data, error } = await (supabase as any)
        .from('locations')
        .select('id, name, logo_url')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Get selected location if type is not 'company'
  const selectedLocation = useMemo(() => {
    if (selectedQRType === 'company') return null;
    return locations.find((loc: any) => loc.id === selectedQRType) || null;
  }, [selectedQRType, locations]);

  // Generate appropriate URL based on selection
  // Use production domain for QR codes (rollops.pro) so they work even in development
  const inviteUrl = useMemo(() => {
    if (!companyId) return '';
    // Use production domain for QR codes so they work everywhere
    const baseUrl = 'rollops.pro';
    if (selectedQRType === 'company') {
      return `https://${baseUrl}/signup/${companyId}`;
    }
    // Include locationId in the URL for location-specific signups
    return `https://${baseUrl}/signup/${companyId}/${selectedQRType}`;
  }, [companyId, selectedQRType]);

  // RollOps logo SVG (fallback when gym logo is not available)
  // Create a simple circular logo with "RO" initials
  const rollopsLogoSvg = useMemo(() => {
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="38" fill="#9B1421" stroke="#000" stroke-width="2"/>
        <text x="40" y="52" font-family="Arial, sans-serif" font-size="32" font-weight="bold" fill="#FFFFFF" text-anchor="middle">RO</text>
      </svg>
    `)}`;
  }, []);

  // Get appropriate logo/avatar based on selection, with RollOps fallback
  const qrLogo = useMemo(() => {
    let logoUrl: string | null = null;
    if (selectedQRType === 'company') {
      logoUrl = companyData?.logo_url || null;
    } else {
      logoUrl = selectedLocation?.logo_url || companyData?.logo_url || null;
    }
    
    // If no logo or empty string, use RollOps logo
    if (!logoUrl || logoUrl.trim() === '') {
      return rollopsLogoSvg;
    }
    
    return logoUrl;
  }, [selectedQRType, selectedLocation, companyData, rollopsLogoSvg]);

  // Track logo load error to fallback to RollOps logo
  const [logoError, setLogoError] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  // Convert external logo to data URL to avoid CORS issues during printing
  useEffect(() => {
    if (qrLogo && qrLogo !== rollopsLogoSvg && !logoError) {
      // If it's already a data URL (like rollopsLogoSvg), use it directly
      if (qrLogo.startsWith('data:')) {
        setLogoDataUrl(qrLogo);
        return;
      }

      // Create AbortController to handle race conditions
      const controller = new AbortController();

      // Convert external image to data URL
      fetch(qrLogo, { signal: controller.signal })
        .then(response => response.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onload = () => {
            setLogoDataUrl(reader.result as string);
          };
          reader.readAsDataURL(blob);
        })
        .catch((error) => {
          // Don't log or handle aborted requests
          if (error.name === 'AbortError') {
            return;
          }
          if (process.env.NODE_ENV !== 'production') {
            devWarn('[QR Logo] Failed to convert logo to data URL, using fallback');
          }
          setLogoDataUrl(rollopsLogoSvg);
        });

      // Cleanup function to abort previous fetch when qrLogo changes
      return () => {
        controller.abort('QR logo fetch aborted');
      };
    } else {
      setLogoDataUrl(qrLogo || rollopsLogoSvg);
    }
  }, [qrLogo, rollopsLogoSvg, logoError]);

  const effectiveLogo = logoError ? rollopsLogoSvg : (logoDataUrl || qrLogo);

  // Get appropriate display name
  const displayName = useMemo(() => {
    if (selectedQRType === 'company') return companyData?.name || 'Company';
    return selectedLocation?.name || 'Location';
  }, [selectedQRType, selectedLocation, companyData]);

  const canRenderQr = inviteUrl.length > 0 && companyId;

  const continuePrintFlow = (qrDataUrl: string) => {
    // Use a more reliable approach: create a hidden iframe, write content, then print
    try {
      // Remove any existing print iframe
      const existingIframe = document.getElementById('qr-print-iframe');
      if (existingIframe) {
        existingIframe.remove();
      }

      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.id = 'qr-print-iframe';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      document.body.appendChild(iframe);

      // Wait for iframe to be ready
      iframe.onload = () => {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          console.error('[QR Print] Failed to access iframe document');
          document.body.removeChild(iframe);
          alert('Unable to open print dialog. Please check your browser\'s popup blocker settings.');
          return;
        }

        // Write HTML content to iframe
        iframeDoc.open();
        iframeDoc.write(getPrintHTML(qrDataUrl));
        iframeDoc.close();

        // Wait for image to load, then trigger print
        setTimeout(() => {
          const imgElement = iframeDoc.querySelector('img.qr') as HTMLImageElement | null;
          if (imgElement) {
            imgElement.onload = () => {
              setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                // Clean up iframe after a delay
                setTimeout(() => {
                  if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                  }
                }, 1000);
              }, 100);
            };
            // If image already loaded, trigger print immediately
            if (imgElement.complete) {
              setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                  if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                  }
                }, 1000);
              }, 100);
            }
          } else {
            // Fallback: print even if image check fails
            setTimeout(() => {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
              setTimeout(() => {
                if (iframe.parentNode) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            }, 500);
          }
        }, 100);
      };

      // Set src to trigger onload (some browsers need this)
      iframe.src = 'about:blank';
    } catch (error) {
      console.error('[QR Print] Error during print:', error);
      showToast('An error occurred while trying to print. Please try again.', 'error');
    }
  };

  const handlePrint = () => {
    if (!qrRef.current) {
      console.error('[QR Print] qrRef.current is null');
      return;
    }

    // Try to generate a new high-quality QR code first (better logo quality)
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    // Import React DOM and create high-quality QR code
    import('react-dom/client').then(({ createRoot }) => {
      const root = createRoot(tempDiv);
      root.render(
        React.createElement(QRCodeCanvas, {
          value: inviteUrl,
          size: 1200, // High resolution for printing
          level: 'H', // Highest error correction
          includeMargin: true,
          imageSettings: effectiveLogo && effectiveLogo.startsWith('data:') ? {
            src: effectiveLogo,
            height: 200,
            width: 200,
            excavate: true,
          } : undefined,
        })
      );

      // Wait for render, then get the high-quality canvas
      setTimeout(() => {
        const highQualityCanvas = tempDiv.querySelector('canvas');
        if (highQualityCanvas) {
          try {
            const qrDataUrl = highQualityCanvas.toDataURL('image/png', 1.0);
            root.unmount();
            document.body.removeChild(tempDiv);
            devLog('[QR Print] High-quality QR generated with large logo, length:', qrDataUrl.length);
            continuePrintFlow(qrDataUrl);
          } catch (error) {
            console.error('[QR Print] Failed to generate high-quality QR:', error);
            root.unmount();
            document.body.removeChild(tempDiv);
            fallbackToScaledCanvas();
          }
        } else {
          if (process.env.NODE_ENV !== 'production') {
            devWarn('[QR Print] High-quality canvas not found, falling back to scaled version');
          }
          root.unmount();
          document.body.removeChild(tempDiv);
          fallbackToScaledCanvas();
        }
      }, 300); // Longer wait for high-quality render
    }).catch((importError) => {
      console.error('[QR Print] Failed to import React DOM, using fallback:', importError);
      document.body.removeChild(tempDiv);
      fallbackToScaledCanvas();
    });

    // Fallback function that scales the existing canvas
    const fallbackToScaledCanvas = () => {
      const existingCanvas = qrRef.current?.querySelector('canvas');
      if (!existingCanvas) {
        console.error('[QR Print] Fallback failed: Canvas not found in qrRef');
        showToast('Unable to generate QR code for printing. Please try downloading instead.', 'error');
        return;
      }

      try {
        const scaleFactor = 3;
        const highResCanvas = document.createElement('canvas');
        const ctx = highResCanvas.getContext('2d');

        if (!ctx) {
          throw new Error('Unable to get canvas context');
        }

        highResCanvas.width = existingCanvas.width * scaleFactor;
        highResCanvas.height = existingCanvas.height * scaleFactor;

        // Configure for best quality
        ctx.imageSmoothingEnabled = true;
        if ('imageSmoothingQuality' in ctx) {
          ctx.imageSmoothingQuality = 'high';
        }

        ctx.drawImage(existingCanvas, 0, 0, highResCanvas.width, highResCanvas.height);

        const qrDataUrl = highResCanvas.toDataURL('image/png', 1.0);
        devLog('[QR Print] Fallback: Scaled QR generated, length:', qrDataUrl.length);
        continuePrintFlow(qrDataUrl);
      } catch (error) {
        console.error('[QR Print] Fallback scaling failed:', error);
        showToast('Unable to generate QR code for printing. Please try downloading instead.', 'error');
      }
    };
  };

  const getPrintHTML = (qrDataUrl: string) => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${displayName} - Signup QR Code</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; 
              background: #fff; 
              padding: 32px; 
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
            }
            .wrap { 
              max-width: 900px; 
              margin: 0 auto; 
              text-align: center; 
            }
            h1 { 
              margin: 0 0 8px 0; 
              font-size: 56px; 
              line-height: 1.1; 
              color: #000;
            }
            .location-name { 
              margin: 0 0 24px 0; 
              font-size: 32px; 
              color: #666; 
              font-weight: 500; 
            }
            .qr {
              display: block;
              margin: 0 auto;
              width: 72vw;
              height: 72vw;
              max-width: 1000px;
              max-height: 1000px;
              border: 2px solid #000;
              padding: 16px;
              background: #fff;
            }
            .powered { 
              margin-top: 16px; 
              font-size: 14px; 
              color: #666; 
            }
            .rollops-logo {
              font-family: Arial, sans-serif;
              font-size: 24px;
              font-weight: bold;
              color: #000;
              margin-top: 8px;
            }
            .rollops-logo span {
              color: #E8616C;
            }
            .logo {
              display: none;
            }
            @media print {
              body { padding: 0; }
              .wrap {
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .text-section { order: -1; }
              .qr {
                width: 18cm;
                height: 18cm;
                order: 0;
              }
              .powered-section {
                order: 1;
                margin-top: 1cm;
                padding-top: 1cm;
                border-top: 1px solid #ddd;
                text-align: center;
              }
              h1 { font-size: 64px; }
              .location-name { font-size: 28px; }
              .powered-section p {
                font-size: 14px;
                color: #666;
                margin-bottom: 8px;
              }
              .rollops-logo {
                font-size: 24px;
                font-weight: bold;
                color: #000;
              }
              .rollops-logo span {
                color: #9B1421;
              }
            }

            /* Landscape orientation support */
            @media print and (orientation: landscape) {
              .wrap {
                flex-direction: row;
                justify-content: center;
                gap: 1cm;
                max-width: none;
                padding: 0.5cm;
              }
              .text-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                max-width: 8cm;
                order: -1;
              }
              .qr {
                width: 18cm;
                height: 18cm;
                flex-shrink: 0;
                order: 0;
              }
              .powered-section {
                order: 1;
                margin-top: 0;
                padding-top: 0;
                border-top: none;
                position: relative;
              }
              .powered-section p {
                font-size: 12px;
                margin-bottom: 4px;
              }
              .rollops-logo {
                font-size: 20px;
              }
              h1 {
                font-size: 56px;
                margin: 0 0 0.5cm 0;
                line-height: 1.1;
              }
              .location-name {
                font-size: 28px;
                margin: 0 0 0.5cm 0;
                color: #666;
                font-weight: 500;
              }
            }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="text-section">
              <h1>Signup Here</h1>
              ${selectedQRType !== 'company' ? `<p class="location-name">${displayName}</p>` : ''}
            </div>
            <img class="qr" src="${qrDataUrl}" alt="QR Code" onload="devLog('QR image loaded')" onerror="console.error('QR image failed to load')" />
            <div class="powered-section">
              <p>Powered by</p>
              <div class="rollops-logo">
                Roll<span>O</span>ps
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handleDownload = () => {
    if (!qrRef.current) return;

    const qrCanvas = qrRef.current.querySelector('canvas');
    if (!qrCanvas) return;

    const qrDataUrl = qrCanvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `${displayName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_qr_signup.png`;
    link.href = qrDataUrl;
    link.click();
  };

  return (
    <>
      <ToastContainer />
      <div className="flex-1 flex flex-col min-h-0">
        {/* Page Header - matches Dashboard styling */}
        <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Signup QR Codes</h1>
              <p className="text-white/80 mt-1">Generate and print QR codes for easy student signups</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handlePrint}
                disabled={!canRenderQr}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                size="sm"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={handleDownload}
                disabled={!canRenderQr}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>

        {/* Content - full height, scrollable */}
        <div className="flex-1 bg-[#111010] overflow-auto min-h-0 py-6 space-y-6">
        {/* QR Code Type Selector */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-text-primary">Select QR Code Type</CardTitle>
          </CardHeader>
          <CardContent>
            {locationsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421] mx-auto" />
                <p className="mt-2 text-text-tertiary text-sm">Loading locations...</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setSelectedQRType('company')}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                    selectedQRType === 'company'
                      ? 'bg-[#9B1421] border-[#9B1421] text-white'
                      : 'bg-[#363232] border-[#504A4A] text-text-primary hover:border-[#9B1421]'
                  }`}
                >
                  Company QR
                </button>
                {locations.map((location: any) => (
                  <button
                    key={location.id}
                    onClick={() => setSelectedQRType(location.id)}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      selectedQRType === location.id
                        ? 'bg-[#9B1421] border-[#9B1421] text-white'
                        : 'bg-[#363232] border-[#504A4A] text-text-primary hover:border-[#9B1421]'
                    }`}
                  >
                    {location.name}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* QR Code Display */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardContent className="pt-6">
            {canRenderQr ? (
              <div ref={qrRef} className="flex flex-col items-center justify-center py-8">
                <h2 className="text-xl font-semibold text-text-primary mb-6">{displayName}</h2>
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeCanvas
                    value={inviteUrl}
                    size={400}
                    level="H"
                    includeMargin
                    imageSettings={
                      effectiveLogo
                        ? {
                            src: effectiveLogo,
                            height: 80,
                            width: 80,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>
                {/* Hidden image to detect logo load errors */}
                {qrLogo && qrLogo !== rollopsLogoSvg && (
                  <img
                    src={qrLogo}
                    alt=""
                    className="hidden"
                    onError={() => setLogoError(true)}
                    onLoad={() => setLogoError(false)}
                  />
                )}
                <p className="mt-6 text-sm text-text-secondary text-center max-w-md">
                  Scan this QR code to sign up for {displayName}
                </p>
                <p className="mt-2 text-xs text-text-tertiary text-center max-w-md break-all">
                  {inviteUrl}
                </p>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-text-secondary">Select a company or location to generate a QR code</p>
              </div>
            )}
            
            {/* Powered by RollOps - Always shown when QR is rendered */}
            {canRenderQr && (
              <div className="pt-6 border-t border-[#504A4A]">
                <div className="flex flex-col items-center justify-center gap-2">
                  <p className="text-xs text-text-tertiary">Powered by</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-text-primary">
                      Roll<span className="text-rollops-accent">O</span>ps
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-text-primary">How to Use</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-start gap-2">
                <span className="text-rollops-accent mt-1">•</span>
                <span>Select "Company QR" for a general signup code, or choose a specific location</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-rollops-accent mt-1">•</span>
                <span>Click "Print" to generate a print-friendly version with instructions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-rollops-accent mt-1">•</span>
                <span>Click "Download" to save the QR code as an image file</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-rollops-accent mt-1">•</span>
                <span>Display the printed QR code at your front desk or entrance for easy student signups</span>
              </li>
            </ul>
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  );
}
